import type { AssetReference } from '@opallabs/cassini'
import { joinList } from '@opallabs/lambda'
import type { AssetType, Counts, ExplainProps, VariantRules } from './types'

/**
 * Count `assetReferences` by `AssetType`, including alternate logic determined
 * by `variantRules`.
 */
export function countAssetReferencesByType(
  assetReferences: AssetReference[],
  variantRules: VariantRules = {}
): Counts {
  const counts: Counts = {
    audio: 0,
    file: 0,
    gif: 0,
    image: 0,
    video: 0,
  }

  for (const assetReference of assetReferences) {
    switch (assetReference.asset_type) {
      // note that this exploits case fallthrough
      case 'audio':
      case 'file':
      case 'video':
        counts[assetReference.asset_type] += 1
        break
      // By default everything is an image, possibly adjusted by variant rules.
      default:
        if (variantRules.gifsAreNotImages && assetReference.format === 'gif') {
          counts.gif += 1
        } else if (variantRules.imagesAreVideos) {
          counts.video += 1
        } else {
          counts.image += 1
        }
        break
    }
  }

  return counts
}

/**
 * @note This is specific to English.
 */
function formatAssetTypeCount(assetType: AssetType, count: number): string {
  let outputType: string
  switch (assetType) {
    case 'audio':
      outputType = `${assetType} file`
      break
    case 'file':
      outputType = 'document'
      break
    default:
      outputType = assetType
      break
  }

  let output: string

  if (count === 0) output = outputType
  else output = `${count} ${outputType}`

  return count === 1 ? output : `${output}s`
}

/**
 * Check per-`AssetType` `limits`, and return a string explaining any limits
 * that have been exceeded by the given `assetReferences` (with consideration
 * for `variantRules`). If `assetReferences` is fully within `limits`, returns
 * `null`.
 *
 * @example
 * explainAssetReferenceValidationErrors({
 *   assetReferences, // 5 `AssetReference`s with `asset_type: 'image'`
 *   contentTypeName: 'Twitter Tweet',
 *   limits: {
 *     image: 4,
 *   },
 * })
 * //=> "Twitter Tweet has a limit of 4 images"
 */
export const explainAssetReferenceValidationErrors = ({
  assetReferences,
  contentTypeName,
  limits = {},
  variantRules = {},
}: ExplainProps): string | null => {
  if (Object.values(limits).length === 0) return null

  const counts = countAssetReferencesByType(assetReferences, variantRules)
  const notAllowed: string[] = []
  const tooMany: string[] = []
  for (const [type, count] of Object.entries(counts)) {
    const safeType = type as AssetType
    if (limits[safeType] === undefined) continue

    const limit = limits[safeType] as number
    if (limit >= count) continue

    const formatted = formatAssetTypeCount(safeType, limit)
    if (limit === 0) notAllowed.push(formatted)
    else tooMany.push(formatted)
  }

  const errors: string[] = []
  if (notAllowed.length > 0) {
    errors.push(`doesn’t allow ${joinList(notAllowed, 'or')}`)
  }
  if (tooMany.length > 0) {
    errors.push(`has a limit of ${joinList(tooMany)}`)
  }
  if (errors.length === 0) return null

  return [contentTypeName, errors.join(', and ')].join(' ')
}
